export interface CSPRule {
  source: string;
  headers: Array<{
    key: string;
    value: string;
  }>;
}

export interface CSPConfig {
  assetPrefix: string;
  imagePath: string;
  cspRule: CSPRule[];
}

export type Environment = "hml" | "blue" | "production";

const ENVIRONMENT_DOMAINS: Record<Environment, string> = {
  hml: "https://hportal.webmotors.com.br",
  blue: "https://azulportal.webmotors.com.br",
  production: "https://www.webmotors.com.br",
} as const;

const CSP_DIRECTIVES = {
  defaultSrc: [
    "https:",
    "https://*.api.useinsider.com/",
    "https://*.webmotors.com.br/insider/insider-sw-sdk.js",
    "https://*.webmotors.com.br/*",
    "'unsafe-inline'",
  ],

  scriptSrc: [
    "https:",
    "https://www.datadoghq-browser-agent.com/",
    "https://cdn.taboola.com/",
    "https://dynamic.criteo.com/",
    "https://www.googleadservices.com/",
    "https://sb.scorecardresearch.com/",
    "https://googleads.g.doubleclick.net/",
    "https://connect.facebook.net/",
    "https://tags.creativecdn.com/",
    "https://securepubads.g.doubleclick.net/",
    "'unsafe-eval'",
    "'unsafe-inline'",
  ],

  scriptSrcElem: [
    "https:",
    "https://cdn.taboola.com/",
    "https://tags.creativecdn.com/",
    "https://googleads.g.doubleclick.net/",
    "https://securepubads.g.doubleclick.net/",
    "https://dynamic.criteo.com/",
    "https://survey.survicate.com/",
    "https://webmotors.api.useinsider.com/",
    "https://pixel.byspotify.com/",
    "https://connect.facebook.net/",
    "'unsafe-inline'",
  ],

  styleSrc: ["https:", "'unsafe-inline'"],

  workerSrc: [
    "blob:",
    "https://*.api.useinsider.com/",
    "https://*.webmotors.com.br/insider/insider-sw-sdk.js",
  ],

  imgSrc: ["https:", "data:", "blob:", "https://sync.bidence.net/"],

  mediaSrc: ["data:"],

  connectSrc: [
    "https:",
    "wss://*.webmotors.com.br/",
    "https://dpm.demdex.net/",
    "https://webmotors.tt.omtrdc.net/",
    "https://webmotors.sc.omtrdc.net/",
    "https://api.trafficguard.ai/",
  ],

  fontSrc: [
    "'self'",
    "data:",
    "https://fonts.gstatic.com/",
    "https://surveys-static.survicate.com/",
    "https://use.typekit.net/",
    "https://*.webmotors.com.br/*",
  ],
} as const;

function buildCSPPolicy(): string {
  const directives = [
    `default-src ${CSP_DIRECTIVES.defaultSrc.join(" ")}`,
    `script-src ${CSP_DIRECTIVES.scriptSrc.join(" ")}`,
    `script-src-elem ${CSP_DIRECTIVES.scriptSrcElem.join(" ")}`,
    `style-src ${CSP_DIRECTIVES.styleSrc.join(" ")}`,
    `worker-src ${CSP_DIRECTIVES.workerSrc.join(" ")}`,
    `img-src ${CSP_DIRECTIVES.imgSrc.join(" ")}`,
    `media-src ${CSP_DIRECTIVES.mediaSrc.join(" ")}`,
    `connect-src ${CSP_DIRECTIVES.connectSrc.join(" ")}`,
    `font-src ${CSP_DIRECTIVES.fontSrc.join(" ")}`,
  ];

  return directives.join("; ");
}

const cspRules: CSPRule[] = [
  {
    source: "/:path*",
    headers: [
      {
        key: "Content-Security-Policy",
        value: buildCSPPolicy(),
      },
    ],
  },
];

function isValidEnvironment(environment?: string): environment is Environment {
  return environment !== undefined && environment in ENVIRONMENT_DOMAINS;
}

function buildAssetPrefix(domain: string, path?: string): string {
  return path ? `${domain}/${path}` : domain;
}

function buildImagePath(domain: string, path?: string): string {
  return `${buildAssetPrefix(domain, path)}/_next/image`;
}

export function addCSPSource(
  directive: keyof typeof CSP_DIRECTIVES,
  source: string
): void {
  const directiveArray = CSP_DIRECTIVES[directive] as unknown as string[];
  directiveArray.push(source);
}

export function getAvailableEnvironments(): Environment[] {
  return Object.keys(ENVIRONMENT_DOMAINS) as Environment[];
}

/**
 * Get configuration for current environment
 * @param environment - Environment name (hml, blue, production)
 * @param path - Optional path to append to the domain
 * @returns Configuration object with assetPrefix, imagePath, and cspRule
 */
export function getConfig({
  environment,
  domain: configDomain,
  path,
}: {
  environment?: string;
  domain?: string;
  path?: string;
}): CSPConfig {
  if (!isValidEnvironment(environment)) {
    return {
      assetPrefix: "",
      imagePath: "/_next/image",
      cspRule: [],
    };
  }

  const domain = configDomain ?? ENVIRONMENT_DOMAINS[environment];

  return {
    assetPrefix: buildAssetPrefix(domain, path),
    imagePath: buildImagePath(domain, path),
    cspRule: cspRules,
  };
}
